import React, { useEffect, useState } from 'react';
import { Modal } from './ui';
import { useSettingsStore, useUserStore } from '../store';
import { useTelegram } from '../hooks/useTelegram';
import { api } from '../services/api';
import { BalanceInfo, LoyaltyTier } from '../types';
import { TrophyIcon, tierByIndex } from './TrophyIcon';

const BLUE = '#3657F0';
const fmt = (n: number) => n.toLocaleString('ru-RU');

// Блоки «Бонусный баланс» (с уровнем кешбэка → всплывашка) и «Реферальная
// программа» — верх страницы профиля.
export const CashbackBlocks: React.FC = () => {
  const { settings } = useSettingsStore();
  const { openTelegramLink } = useTelegram();

  const [balanceInfo, setBalanceInfo] = useState<BalanceInfo | null>(null);
  // Последний известный баланс из хранилища: показываем его, пока грузится свежий,
  // иначе на секунду виден ноль
  const cachedBonus = useUserStore((s) => s.cachedBonus);
  const setCachedBonus = useUserStore((s) => s.setCachedBonus);
  const [refCopied, setRefCopied] = useState(false);
  const [showTiers, setShowTiers] = useState(false);
  const [showFriends, setShowFriends] = useState(false);

  useEffect(() => {
    api.getBalance()
      .then((b) => { setBalanceInfo(b); setCachedBonus(b.balance); })
      .catch(() => {});
  }, []);

  const friendDiscount = settings.referral?.friendDiscountPercent ?? 3;
  const refFirst = settings.referral?.refFirstPercent ?? 3;

  const loyalty = balanceInfo?.loyalty;
  const tiers: LoyaltyTier[] = loyalty?.tiers ?? [];
  const spentRub = loyalty?.spentRub ?? 0;
  const currentPercent = loyalty?.currentPercent ?? (settings.referral?.cashbackPercent ?? 3);
  const currentTierName = loyalty?.currentTierName ?? null;
  const nextTier = tiers.find((t) => spentRub < t.minSpentRub) || null;

  if (settings.referral?.enabled === false) return null;

  // Clipboard API в Telegram WebView часто недоступен (нет разрешения/не secure
  // context) — тогда копируем через скрытый textarea + execCommand.
  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch { /* фолбэк ниже */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '0';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const handleCopyRefLink = async () => {
    if (!balanceInfo) return;
    const ok = await copyToClipboard(balanceInfo.refLinkBot);
    if (!ok) return;
    setRefCopied(true);
    setTimeout(() => setRefCopied(false), 2000);
  };

  // Шаринг карточкой (фото + текст со ссылкой) через инлайн-режим бота.
  // ВАЖНО: openTelegramLink принимает ТОЛЬКО t.me — внутренняя ссылка клиента.
  const handleShareRefLink = () => {
    if (!balanceInfo) return;
    try {
      const wa = (window as any).Telegram?.WebApp;
      if (wa?.switchInlineQuery) {
        wa.switchInlineQuery(balanceInfo.refCode, ['users', 'groups', 'channels']);
        return;
      }
    } catch { /* фолбэк ниже */ }
    const text = `🎮 Скидка ${friendDiscount}% на первый заказ в XPlay Store — игры и подписки PlayStation дешевле, чем в PS Store!`;
    openTelegramLink(
      `https://t.me/share/url?url=${encodeURIComponent(balanceInfo.refLinkBot)}&text=${encodeURIComponent(text)}`
    );
  };

  const formatExpiryDate = (iso: string) => {
    try {
      const d = new Date(iso);
      return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
    } catch {
      return '';
    }
  };

  const refLinkDisplay = (balanceInfo?.refLinkBot || '').replace(/^https?:\/\//, '');

  return (
    <>
      {/* Бонусный баланс */}
      <div className="bg-white p-5 text-center mb-3" style={{ borderRadius: 20, border: '1px solid #E1E3E5' }}>
        <p style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.08em', color: '#8B8E97' }}>
          БОНУСНЫЙ БАЛАНС
        </p>
        {balanceInfo || cachedBonus !== null ? (
          <p className="mt-1" style={{ fontFamily: 'Manrope, sans-serif', fontSize: 44, fontWeight: 800, lineHeight: '110%', color: '#1A1D26' }}>
            {fmt(balanceInfo?.balance ?? cachedBonus ?? 0)} ₽
          </p>
        ) : (
          <div className="mt-2 mx-auto animate-pulse" style={{ width: 132, height: 40, borderRadius: 12, background: '#F2F2F7' }} />
        )}
        {balanceInfo?.frozen && (
          <span className="inline-block mt-1.5 text-[12px] font-semibold px-2.5 py-1 rounded-lg" style={{ background: '#FFF4E5', color: '#B45309' }}>
            Баланс на проверке
          </span>
        )}
        {balanceInfo?.expiringSoon && (
          <p className="mt-1.5" style={{ fontSize: 12, color: '#B45309' }}>
            ⏳ {fmt(balanceInfo.expiringSoon.amount)} ₽ сгорят до {formatExpiryDate(balanceInfo.expiringSoon.date)}
          </p>
        )}

        {/* Две плитки: свой кешбэк и бонус за друга */}
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => { if (tiers.length > 0) setShowTiers(true); }}
            className={`flex-1 min-w-0 text-left ${tiers.length > 0 ? 'active:scale-[0.98] transition-transform' : 'cursor-default'}`}
            style={{ background: '#F2F2F7', borderRadius: 14, padding: '12px 14px' }}
          >
            <span className="block" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: '#8B8E97' }}>
              КЕШБЭК
            </span>
            <span className="block mt-0.5" style={{ fontSize: 15, fontWeight: 700, color: '#1A1D26' }}>
              <span style={{ color: BLUE }}>{currentPercent}%</span> с покупки
            </span>
          </button>
          <div className="flex-1 min-w-0 text-left" style={{ background: '#F2F2F7', borderRadius: 14, padding: '12px 14px' }}>
            <span className="block" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: '#8B8E97' }}>
              С ДРУГА
            </span>
            <span className="block mt-0.5" style={{ fontSize: 15, fontWeight: 700, color: '#1A1D26' }}>
              <span style={{ color: BLUE }}>{refFirst}%</span> бонус
            </span>
          </div>
        </div>
      </div>

      {/* Реферальная программа */}
      <div className="bg-white p-5 mb-3" style={{ borderRadius: 20, border: '1px solid #E1E3E5' }}>
        <h2 style={{ fontFamily: 'Manrope, sans-serif', fontSize: 19, fontWeight: 800, color: '#1A1D26' }}>
          Реферальная программа
        </h2>
        <p className="mt-1" style={{ fontSize: 13, lineHeight: '18px', color: '#8B8E97' }}>
          Друг получит скидку {friendDiscount}%, вы — {refFirst}% с его первого заказа и бонусы со всех следующих
        </p>

        <div className="flex gap-2 mt-3.5">
          <div
            className="flex-1 min-w-0 flex items-center px-3"
            style={{ height: 44, borderRadius: 12, background: '#F2F2F7' }}
          >
            <span className="truncate" style={{ fontSize: 13, color: '#5B6472' }}>{refLinkDisplay || '—'}</span>
          </div>
          {/* Обе иконки в DOM: кроссфейд с легким масштабом вместо резкой подмены */}
          <button
            onClick={handleCopyRefLink}
            aria-label={refCopied ? 'Ссылка скопирована' : 'Копировать ссылку'}
            className="relative flex-shrink-0 active:scale-[0.97] transition-transform"
            style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(90deg, #5370F5, #3657F0)' }}
          >
            <span
              className="absolute inset-0 flex items-center justify-center"
              style={{
                opacity: refCopied ? 0 : 1,
                transform: refCopied ? 'scale(0.5)' : 'scale(1)',
                transition: 'opacity 0.18s ease, transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1)',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="11" height="11" rx="2.5" />
                <path d="M5 15H4.5A1.5 1.5 0 013 13.5v-9A1.5 1.5 0 014.5 3h9A1.5 1.5 0 0115 4.5V5" />
              </svg>
            </span>
            <span
              className="absolute inset-0 flex items-center justify-center"
              style={{
                opacity: refCopied ? 1 : 0,
                transform: refCopied ? 'scale(1)' : 'scale(0.5)',
                transition: 'opacity 0.18s ease, transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1)',
              }}
            >
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 13l4 4L19 7" />
              </svg>
            </span>
          </button>
          <button
            onClick={handleShareRefLink}
            className="flex-shrink-0 flex items-center justify-center active:scale-[0.97] transition-transform"
            style={{ width: 44, height: 44, borderRadius: 12, background: '#EEF1FE' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 15V3" />
              <path d="M8 7l4-4 4 4" />
              <path d="M4 13v6a2 2 0 002 2h12a2 2 0 002-2v-6" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3">
          <button
            onClick={() => setShowFriends((v) => !v)}
            className="text-left active:scale-[0.98] transition-transform"
            style={{ background: '#F2F2F7', borderRadius: 14, padding: '12px 14px' }}
          >
            <div className="flex items-center gap-1">
              <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: 20, fontWeight: 800, color: '#1A1D26' }}>
                {balanceInfo?.stats.joined ?? 0}
              </span>
              <svg
                width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#B0B3BA"
                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ transform: showFriends ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
            </div>
            <p style={{ fontSize: 12, lineHeight: '15px', color: '#8B8E97', marginTop: 2 }}>Приглашенных друзей</p>
          </button>
          <div style={{ background: '#F2F2F7', borderRadius: 14, padding: '12px 14px' }}>
            <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: 20, fontWeight: 800, color: '#1A1D26' }}>
              {fmt(balanceInfo?.stats.earnedTotal ?? 0)} ₽
            </span>
            <p style={{ fontSize: 12, lineHeight: '15px', color: '#8B8E97', marginTop: 2 }}>Заработано с рефералов</p>
          </div>
        </div>

        {/* Детализация по друзьям — раскрывается тапом по «Приглашенных друзей».
            Плавность дает анимация grid-template-rows 0fr→1fr: высота считается
            по содержимому, подгонять max-height вручную не нужно. */}
        <div
          style={{
            display: 'grid',
            gridTemplateRows: showFriends ? '1fr' : '0fr',
            opacity: showFriends ? 1 : 0,
            transition: 'grid-template-rows 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.22s ease',
          }}
        >
          <div style={{ overflow: 'hidden' }}>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div style={{ border: '1px solid #E7E8EC', borderRadius: 14, padding: '12px 14px' }}>
                <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: 20, fontWeight: 800, color: '#1A1D26' }}>
                  {balanceInfo?.stats.clicks ?? 0}
                </span>
                <p style={{ fontSize: 12, lineHeight: '15px', color: '#8B8E97', marginTop: 2 }}>Перешло по ссылке</p>
              </div>
              <div style={{ border: '1px solid #E7E8EC', borderRadius: 14, padding: '12px 14px' }}>
                <span style={{ fontFamily: 'Manrope, sans-serif', fontSize: 20, fontWeight: 800, color: '#1A1D26' }}>
                  {balanceInfo?.stats.purchased ?? 0}
                </span>
                <p style={{ fontSize: 12, lineHeight: '15px', color: '#8B8E97', marginTop: 2 }}>Совершили покупку</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Всплывашка «Уровни кешбэка» */}
      <Modal isOpen={showTiers} onClose={() => setShowTiers(false)} title="Уровни кешбэка">
        <div className="pb-1">
          {/* Коллекция трофеев: 4 секции-прогресса. Достигнутый уровень залит целиком,
              следующий заполняется пропорционально тратам от прошлого порога. */}
          <div className="grid grid-cols-4" style={{ gap: 6 }}>
            {tiers.map((t, i) => {
              const reached = spentRub >= t.minSpentRub;
              const prevMin = i > 0 ? tiers[i - 1].minSpentRub : 0;
              const fill = reached
                ? 1
                : spentRub <= prevMin
                  ? 0
                  : (spentRub - prevMin) / (t.minSpentRub - prevMin);
              return (
                <div
                  key={i}
                  className="relative flex items-center justify-center overflow-hidden"
                  style={{ borderRadius: 14, background: '#F2F2F7', height: 74 }}
                >
                  <div
                    className="absolute inset-y-0 left-0"
                    style={{ width: `${Math.round(fill * 100)}%`, background: '#DCE4FB', transition: 'width 0.4s ease' }}
                  />
                  <span className="relative">
                    <TrophyIcon tier={tierByIndex(i)} size={38} reached={reached} />
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-center" style={{ fontSize: 12, color: '#B0B3BA', marginTop: 8 }}>
            Ваша коллекция растет с покупками
          </p>

          {/* Список уровней */}
          <div className="flex flex-col gap-2 mt-3.5">
            {tiers.map((t, i) => {
              const reached = spentRub >= t.minSpentRub;
              const isCurrent = currentTierName
                ? t.name === currentTierName
                : reached && (i === tiers.length - 1 || spentRub < tiers[i + 1].minSpentRub);
              // «Осталось N ₽» — только на финишной прямой (5000 ₽ и меньше до уровня)
              const remaining = t.minSpentRub - spentRub;
              const isNext = (nextTier ? t.minSpentRub === nextTier.minSpentRub : false) && remaining <= 5000;
              return (
                <div
                  key={i}
                  className="flex items-center gap-3"
                  style={{
                    borderRadius: 16,
                    padding: '13px 14px',
                    border: isCurrent ? `1.5px solid ${BLUE}` : '1px solid #E7E8EC',
                    background: '#FFFFFF',
                  }}
                >
                  <TrophyIcon tier={tierByIndex(i)} size={34} reached={reached} />
                  <div className="flex-1 min-w-0">
                    <p style={{ fontFamily: 'Manrope, sans-serif', fontSize: 16, fontWeight: 800, color: '#1A1D26' }}>
                      {t.name || `Уровень ${i + 1}`} <span style={{ color: BLUE }}>{t.cashbackPercent}%</span>
                    </p>
                    <p style={{ fontSize: 12, color: '#8B8E97', marginTop: 1 }}>
                      {t.minSpentRub === 0 ? 'с первой покупки' : `от ${fmt(t.minSpentRub)} ₽`}
                    </p>
                    {isNext && (
                      <p style={{ fontSize: 12, fontWeight: 600, color: '#EA7A24', marginTop: 1 }}>
                        Осталось {fmt(remaining)} ₽
                      </p>
                    )}
                  </div>
                  {isCurrent && (
                    <span
                      className="flex-shrink-0"
                      style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', color: BLUE, background: '#EEF1FE', borderRadius: 10, padding: '5px 9px' }}
                    >
                      ВЫ ЗДЕСЬ
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
    </>
  );
};
